// Add / Edit a component limitation — the slide-in over the Aircraft Item Details tab
// (component editor, 2026-10-07). Chrome is the shared Sheet (BP-89); the save is
// POST / PATCH /v6/items/{id}/limitations, the rules are app/items/limitations.py.
//
// Legacy (intent): the inline row editor of mod_parts/includes/tabs/parts_tab_item_aircraft.inc
// :598-660 -> save_limitation (parts_ajax_handler.inc:2090-2229). Its fields: Based On, Period,
// Type, Finite Life, Warning + unit, Date On / Due Off, @A/C Unit Fitted, @Install TSN (+ UNK)
// and TSO. @Install TSN is TSN AT INSTALL (total_life_used); the totals are computed, never
// typed, so they are not on this form.
//
// 2026-10-14: the aircraft tab's Edit Limitation modal (aircraft_component_edit.inc) brings
// Position, Reference, the tenant's Based On list, the Limited Life / Overhaul type rules and the
// Warning Unit default; the rules are componentLimitations.ts.
import React, { useMemo, useRef } from "react";
import { Autocomplete, Checkbox, FormControlLabel, Grid, MenuItem, TextField as MuiTextField } from "@mui/material";
import { Field, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { TextField } from "formik-mui";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import { ActionButton, CancelButton } from "../../../components/Buttons";
import Sheet, { SheetActions, SheetContent, SheetHeader } from "../../../components/Sheet";
import TenantDatePicker from "../../../components/TenantDatePicker";
import { useConfig } from "../../../hooks/useConfig";
import { handleAPIErrors } from "../../../utils/FormikErrors";
import { WARNING_UNIT_OPTIONS } from "../../../utils/limitationVocab";
import {
  basedOnChoices,
  ComponentLimitationRow,
  configList,
  defaultWarningUnit,
  emptyLimitationForm,
  isConditionAxis,
  isDateAxis,
  limitationFormErrors,
  limitationFormValues,
  LimitationFormValues,
  limitationPayload,
  limitedLifePeriod,
  typeChoices,
  typeRules,
} from "../componentLimitations";

interface Props {
  open: boolean;
  itemId: number;
  /** The row being edited; null adds a new limitation. */
  row: ComponentLimitationRow | null;
  /** Fitted to an aircraft: the install figures apply. */
  fitted: boolean;
  /** Based On values already on this item (the stored spellings, e.g. "Engine1 Hours"). */
  knownBasedOn: string[];
  onClose: () => void;
  onSaved: () => void;
}

const ComponentLimitationSheet: React.FC<Props> = ({ open, itemId, row, fitted, knownBasedOn, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<FormikProps<LimitationFormValues>>(null);
  const aircraftConfig = useConfig("aircraft");
  const configurationConfig = useConfig("aircraft_configuration");
  // Served prefix-stripped (src/hooks/__tests__/configKeyShape.test.ts).
  const basedOnOptions = useMemo(
    () => basedOnChoices(aircraftConfig?.based_on_list, knownBasedOn, row?.based_on ?? ""),
    [aircraftConfig, knownBasedOn, row]
  );
  const positionOptions = useMemo(
    () => configList(configurationConfig?.component_position_list),
    [configurationConfig]
  );
  const initialValues = useMemo(() => (row ? limitationFormValues(row) : emptyLimitationForm()), [row]);

  const submit = (values: LimitationFormValues, helpers: FormikHelpers<LimitationFormValues>) => {
    const errors = limitationFormErrors(values, fitted);
    if (Object.keys(errors).length) {
      helpers.setErrors(errors);
      helpers.setSubmitting(false);
      return;
    }
    const body = limitationPayload(values, fitted, typeRules(values.limitation_type, aircraftConfig));
    const request = row
      ? axios.patch(`/v6/items/${itemId}/limitations/${row.id}`, body, { cache: false })
      : axios.post(`/v6/items/${itemId}/limitations`, body, { cache: false });
    request
      .then(() => {
        helpers.setSubmitting(false);
        enqueueSnackbar(row ? "Limitation saved" : "Limitation added", { variant: "success" });
        onSaved();
      })
      .catch((error) => {
        // a failed write keeps the sheet open with what was typed (BP-89)
        helpers.setSubmitting(false);
        handleAPIErrors(error, helpers.setErrors);
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "Error saving limitation", { variant: "error" });
      });
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader onClose={onClose} title={row ? `Edit Limitation ${row.id}` : "Add Limitation"} />
      <SheetContent>
        {open ? (
          <Formik initialValues={initialValues} onSubmit={submit} innerRef={formRef} enableReinitialize>
            {({ values, setFieldValue, errors }) => {
              const dateAxis = isDateAxis(values.based_on);
              const condition = isConditionAxis(values.based_on);
              const rules = typeRules(values.limitation_type, aircraftConfig);
              // Overhaul carries no finite life; a stored one stays editable rather than be wiped
              const finiteNA = rules.noLife && !(Number(values.life_hours) > 0);
              const setBasedOn = (value: string) => {
                setFieldValue("based_on", value);
                const unit = defaultWarningUnit(value);
                if (unit) setFieldValue("warning_unit", unit);
              };
              const setTsn = (value: string) => {
                setFieldValue("tsn", value);
                if (rules.noPeriod) setFieldValue("tso", value);
              };
              return (
                <Form className="mui-form" data-testid="component-limitation-form">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        freeSolo
                        options={basedOnOptions}
                        value={values.based_on}
                        onChange={(_e, v) => setBasedOn(typeof v === "string" ? v : "")}
                        onInputChange={(_e, v, reason) => {
                          // "reset" is the field echoing its value (on open, on a pick): not an edit
                          if (reason === "input") setBasedOn(v ?? "");
                          else if (reason === "clear") setFieldValue("based_on", "");
                        }}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            label="Based On"
                            required
                            error={Boolean(errors.based_on)}
                            helperText={errors.based_on}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MuiTextField
                        select
                        fullWidth
                        label="Type"
                        value={values.limitation_type}
                        onChange={(e) => {
                          const type = e.target.value;
                          setFieldValue("limitation_type", type);
                          if (typeRules(type, aircraftConfig).noPeriod) {
                            if (Number(values.life_hours) > 0) setFieldValue("life", values.life_hours);
                            setFieldValue("tso", values.tsn_unknown ? "0" : values.tsn);
                          }
                        }}
                        inputProps={{ "data-testid": "limitation-type" }}
                      >
                        <MenuItem value="">—</MenuItem>
                        {typeChoices(aircraftConfig?.component_type_list, values.limitation_type).map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </MuiTextField>
                    </Grid>
                    {!condition ? (
                      <>
                        <Grid item xs={6}>
                          {rules.noPeriod ? (
                            <MuiTextField
                              fullWidth
                              label="Period"
                              value={limitedLifePeriod(values, rules)}
                              disabled
                              helperText="Limited Life: the Finite Life"
                            />
                          ) : (
                            <Field component={TextField} fullWidth name="life" label="Period" type="number" />
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          {finiteNA ? (
                            <MuiTextField fullWidth label="Finite Life" value="N/A" disabled />
                          ) : (
                            <Field component={TextField} fullWidth name="life_hours" label="Finite Life" type="number" />
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Field component={TextField} fullWidth name="warning" label="Warning" type="number" />
                        </Grid>
                        <Grid item xs={6}>
                          <Field component={TextField} select fullWidth name="warning_unit" label="Warning Unit">
                            <MenuItem value="">—</MenuItem>
                            {Array.from(new Set([...WARNING_UNIT_OPTIONS, values.warning_unit].filter(Boolean))).map(
                              (u) => (
                                <MenuItem key={u} value={u}>
                                  {u}
                                </MenuItem>
                              )
                            )}
                          </Field>
                        </Grid>
                      </>
                    ) : null}
                    {fitted ? (
                      <Grid item xs={12}>
                        <Autocomplete
                          freeSolo
                          options={positionOptions}
                          value={values.position}
                          onChange={(_e, v) => setFieldValue("position", typeof v === "string" ? v : "")}
                          onInputChange={(_e, v, reason) => {
                            if (reason !== "reset") setFieldValue("position", v ?? "");
                          }}
                          renderInput={(params) => (
                            <MuiTextField {...params} label="Position" helperText="Every limitation of this component" />
                          )}
                        />
                      </Grid>
                    ) : null}
                    {fitted ? (
                      <>
                        <Grid item xs={6}>
                          <TenantDatePicker
                            label="Date On"
                            required
                            fullWidth
                            value={values.install_date}
                            onChange={(iso) => setFieldValue("install_date", iso)}
                            error={Boolean(errors.install_date)}
                            helperText={errors.install_date}
                          />
                        </Grid>
                        {!dateAxis ? (
                          <Grid item xs={6}>
                            <Field
                              component={TextField}
                              fullWidth
                              name="aircraft_install"
                              label="@A/C Unit Fitted"
                              type="number"
                              required
                            />
                          </Grid>
                        ) : null}
                      </>
                    ) : null}
                    {dateAxis ? (
                      <Grid item xs={6}>
                        <TenantDatePicker
                          label="Due Off"
                          fullWidth
                          value={values.expiry_date}
                          onChange={(iso) => setFieldValue("expiry_date", iso)}
                          helperText="Blank: worked out from Date On and Period"
                        />
                      </Grid>
                    ) : null}
                    {!condition ? (
                      <>
                        <Grid item xs={6}>
                          <MuiTextField
                            fullWidth
                            name="tsn"
                            label="@Install TSN"
                            type="number"
                            value={values.tsn}
                            onChange={(e) => setTsn(e.target.value)}
                            disabled={values.tsn_unknown}
                            helperText="Time since new when fitted — the total is worked out"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.tsn_unknown}
                                onChange={(e) => {
                                  setFieldValue("tsn_unknown", e.target.checked);
                                  // legacy: UNK zeroes the TSN box, and a Limited Life TSO follows it
                                  if (e.target.checked && rules.noPeriod) setFieldValue("tso", "0");
                                }}
                              />
                            }
                            label="UNK"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            component={TextField}
                            fullWidth
                            name="tso"
                            label="@Install TSO"
                            type="number"
                            disabled={rules.noPeriod}
                            error={Boolean(errors.tso)}
                            helperText={
                              errors.tso ??
                              (rules.noPeriod ? "Limited Life: follows @Install TSN" : "Time since overhaul when fitted")
                            }
                          />
                        </Grid>
                      </>
                    ) : null}
                    {!fitted && !condition ? (
                      <Grid item xs={6}>
                        <Field component={TextField} fullWidth name="time_to_run" label="Time To Run" type="number" />
                      </Grid>
                    ) : null}
                    <Grid item xs={12}>
                      <Field component={TextField} fullWidth name="reference" label="Reference" />
                    </Grid>
                    <Grid item xs={12}>
                      <Field component={TextField} fullWidth multiline minRows={2} name="limitation_notes" label="Notes" />
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        ) : null}
      </SheetContent>
      <SheetActions>
        <CancelButton onClick={onClose} />
        <ActionButton onClick={() => formRef.current?.submitForm()}>{row ? "Save" : "Add"}</ActionButton>
      </SheetActions>
    </Sheet>
  );
};

export default ComponentLimitationSheet;
